import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  Wrench,
  Building2,
  User,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Send,
  ArrowLeft,
  Plus,
  Eye,
  Home,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing", icon: "🔧" },
  { value: "electrical", label: "Electrical", icon: "⚡" },
  { value: "hvac", label: "HVAC / Air Conditioning", icon: "❄️" },
  { value: "structural", label: "Structural", icon: "🏗️" },
  { value: "appliances", label: "Appliances", icon: "🔌" },
  { value: "roofing", label: "Roofing", icon: "🏠" },
  { value: "painting", label: "Painting", icon: "🎨" },
  { value: "flooring", label: "Flooring", icon: "🪵" },
  { value: "doors_windows", label: "Doors & Windows", icon: "🚪" },
  { value: "pest_control", label: "Pest Control", icon: "🐛" },
  { value: "general", label: "General Maintenance", icon: "🔨" },
];

const PRIORITIES = [
  { value: "low", label: "Low - Can wait a few weeks", color: "bg-green-100 text-green-800 border-green-200" },
  { value: "medium", label: "Medium - Within a week", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  { value: "high", label: "High - Within 2-3 days", color: "bg-orange-100 text-orange-800 border-orange-200" },
  { value: "emergency", label: "Emergency - Immediate attention", color: "bg-red-100 text-red-800 border-red-200" },
];

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-gray-100 text-gray-800", icon: Clock },
  in_review: { label: "Being Reviewed", color: "bg-blue-100 text-blue-800", icon: Eye },
  awaiting_landlord: { label: "Awaiting Approval", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
  landlord_approved: { label: "Approved", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  landlord_rejected: { label: "Not Approved", color: "bg-red-100 text-red-800", icon: AlertTriangle },
  in_progress: { label: "Work In Progress", color: "bg-blue-100 text-blue-800", icon: Wrench },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500", icon: AlertTriangle },
};

const TenantMaintenancePortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalInfo, setPortalInfo] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [formData, setFormData] = useState({
    category: "",
    priority: "medium",
    title: "",
    description: "",
    preferred_date: "",
    access_instructions: "",
  });

  useEffect(() => {
    fetchPortalInfo();
  }, [token]);

  const fetchPortalInfo = async () => {
    try {
      setLoading(true);
      const [infoRes, requestsRes] = await Promise.all([
        axios.get(`${API}/maintenance/portal/${token}`),
        axios.get(`${API}/maintenance/portal/${token}/requests`),
      ]);
      setPortalInfo(infoRes.data);
      setRequests(requestsRes.data.requests || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired maintenance link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Please enter a title for your issue");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please describe the issue");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/maintenance/portal/${token}`, formData);
      toast.success("Maintenance request submitted successfully!");
      setShowForm(false);
      setFormData({
        category: "",
        priority: "medium",
        title: "",
        description: "",
        preferred_date: "",
        access_instructions: "",
      });
      fetchPortalInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <Wrench className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500">Loading your maintenance portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Error</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button asChild>
              <Link to="/">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Maintenance Portal</h1>
                <p className="text-sm text-muted-foreground">Report & track issues</p>
              </div>
            </div>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              Buyorent
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Property Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{portalInfo.property_title || "Your Property"}</h2>
                <p className="text-muted-foreground">Reference: {portalInfo.property_ref || "N/A"}</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>Welcome, {portalInfo.tenant_name}</span>
                </div>
              </div>
              <Button onClick={() => setShowForm(true)} data-testid="new-request-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* New Request Form */}
        {showForm && (
          <Card className="mb-6 border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Report Maintenance Issue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium mb-3">What type of issue is it? *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat.value })}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          formData.category === cat.value
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-xl">{cat.icon}</span>
                        <p className="text-sm font-medium mt-1">{cat.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority Selection */}
                <div>
                  <label className="block text-sm font-medium mb-3">How urgent is this? *</label>
                  <div className="space-y-2">
                    {PRIORITIES.map((pri) => (
                      <button
                        key={pri.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: pri.value })}
                        className={`w-full p-3 rounded-lg border text-left transition-all ${
                          formData.priority === pri.value
                            ? `${pri.color} ring-2 ring-offset-1`
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <span className="font-medium">{pri.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-2">Brief title for the issue *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Leaking tap in bathroom"
                    maxLength={100}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2">Describe the issue in detail *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide as much detail as possible about the issue, including location within the property and when it started..."
                    rows={4}
                  />
                </div>

                {/* Preferred Date */}
                <div>
                  <label className="block text-sm font-medium mb-2">Preferred service date (optional)</label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>

                {/* Access Instructions */}
                <div>
                  <label className="block text-sm font-medium mb-2">Access instructions (optional)</label>
                  <Textarea
                    value={formData.access_instructions}
                    onChange={(e) => setFormData({ ...formData, access_instructions: e.target.value })}
                    placeholder="e.g., Contact me 30 minutes before arriving, key is with the guard, etc."
                    rows={2}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1">
                    {submitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Existing Requests */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Your Maintenance Requests</h3>
          {requests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h4 className="font-medium mb-2">No maintenance issues reported</h4>
                <p className="text-muted-foreground mb-4">
                  When you have a maintenance issue, click the button above to report it.
                </p>
                <Button variant="outline" onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Report an Issue
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((req) => {
                const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const category = CATEGORIES.find((c) => c.value === req.category);

                return (
                  <Card
                    key={req.request_id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedRequest(selectedRequest?.request_id === req.request_id ? null : req)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(req.created_at)}
                            </span>
                          </div>
                          <h4 className="font-medium">{req.title}</h4>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <span>{category?.icon}</span>
                            {category?.label || req.category}
                          </p>
                        </div>
                        <ArrowLeft className={`w-5 h-5 text-muted-foreground transition-transform ${selectedRequest?.request_id === req.request_id ? "rotate-90" : "-rotate-90"}`} />
                      </div>

                      {/* Expanded Details */}
                      {selectedRequest?.request_id === req.request_id && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <div>
                            <span className="text-sm font-medium">Description:</span>
                            <p className="text-sm text-muted-foreground mt-1">{req.description}</p>
                          </div>
                          {req.estimated_cost && (
                            <div>
                              <span className="text-sm font-medium">Estimated Cost:</span>
                              <p className="text-sm text-muted-foreground">K {req.estimated_cost.toLocaleString()}</p>
                            </div>
                          )}
                          {req.resolution_notes && (
                            <div>
                              <span className="text-sm font-medium">Resolution:</span>
                              <p className="text-sm text-muted-foreground">{req.resolution_notes}</p>
                            </div>
                          )}
                          {req.completed_at && (
                            <div>
                              <span className="text-sm font-medium">Completed:</span>
                              <p className="text-sm text-muted-foreground">{formatDate(req.completed_at)}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          <p>Powered by Buyorent Real Estate</p>
          <p className="mt-1">Need help? Contact your property manager</p>
        </div>
      </footer>
    </div>
  );
};

export default TenantMaintenancePortal;
